<template>
  <div
    class="v-table"
    :class="{
      'is-loading': loading,
      'header-hidden': hideHeader,
      'v-table--bordered': !noBorder,
      'v-table--editable-cells': editableCells,
      'has-sticky-header': stickyHeader,
      'expand-on-row-click': expandOnRowClick,
    }"
  >
    <slot />

    <Transition appear name="v-fade">
      <div :class="['v-table__wrapper', { 'v-table__wrapper--dynamic-height': isDynamicHeight }]" :style="{ height: px(wrapperHeight) }">
        <table ref="table" class="v-table__inner" :style="{ tableLayout: layout, minHeight: px(minHeight) }">
          <thead v-if="tableColumns.length && !hideHeader">
            <tr>
              <th v-if="checkable && !checkboxRight" class="v-table__cell--checkbox" :style="{ padding }">
                <VCheckbox v-if="headerCheckable" :disabled="isAllUncheckable" :model-value="isAllChecked" @change="checkAll" />
              </th>

              <th v-if="expandable && expandableColumn && !expandableRight" style="width: 35px" :style="{ padding }" />

              <th
                v-for="(column, index) in visibleColumns"
                :key="column.newKey + ':' + index + 'header'"
                :class="[column.props.headerClass]"
                :style="{ padding, width: px(column.props.width), maxWidth: px(column.props.maxWidth) }"
              >
                <div
                  class="th-wrap"
                  :class="{
                    'is-numeric': column.props.numeric,
                    'is-centered': column.props.centered,
                    'is-sortable': column.props.sortable,
                  }"
                >
                  <span class="is-relative">
                    <span @click="column.props.sortable ? sortNext(column.props.prop) : undefined">
                      <Component :is="column.slots.header" v-if="column.slots.header" />
                      <template v-else>{{ column.props.label }}</template>
                    </span>
                    <VSortCarets
                      v-if="column.props.sortable"
                      :model-value="sortBy === column.props.prop ? sortOrder : ''"
                      @update:model-value="sort({ prop: column.props.prop, order: $event })"
                    />
                  </span>
                </div>
              </th>

              <th v-if="expandable && expandableColumn && expandableRight" style="width: 35px" :style="{ padding }" />

              <th v-if="checkable && checkboxRight" class="v-table__cell--checkbox" :style="{ padding }">
                <VCheckbox v-if="headerCheckable" :disabled="isAllUncheckable" :model-value="isAllChecked" @change="checkAll" />
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(row, index) in paginatedData" :key="get(row, rowKey, index)">
              <tr
                :class="[
                  rowClass(row, index),
                  {
                    'is-checked': isRowChecked(row),
                    'is-focused': focusedRowIndex === index,
                    clickable: clickable || (expandable && rowIsExpandable(row)),
                    'is-dragging': currentDragoverIndex === index,
                    'is-expanded': isExpandedRow(row),
                    'is-recently-expanded': recentlyExpanded && handleRowKey(row) === recentlyExpanded,
                  },
                ]"
                :draggable="draggable || checkOnDragover"
                @dblclick="$emit('dblclick', row)"
                @dragend="onDragend(row, index, $event)"
                @dragenter="onDragenter(row, index, $event)"
                @dragleave="onDragleave(row, index, $event)"
                @dragover="onDragover(row, index, $event)"
                @dragstart="onDragstart(row, index, $event)"
                @drop="onDrop(row, index, $event)"
                @mouseenter="$emit('mouseenter', row)"
                @mouseleave="$emit('mouseleave', row)"
              >
                <td
                  v-if="checkable && !checkboxRight"
                  class="v-table__cell--checkbox"
                  :style="{ padding }"
                  @click.prevent.stop="checkOnCheckboxCellClick ? checkRow({ row, index, event: $event }) : ''"
                >
                  <VCheckbox :disabled="!isRowCheckable(row)" :model-value="isRowChecked(row)" @click.prevent.stop="checkRow({ row, index, event: $event })" />
                </td>

                <td v-if="expandable && expandableColumn && !expandableRight" class="chevron-cell" :style="{ padding }" @click.stop="toggleExpand(row)">
                  <VIcon v-if="rowIsExpandable(row)" icon="ChevronRight" :size="12" />
                </td>

                <template v-for="(column, colindex) in visibleColumns" :key="`${column.newKey}:${index}:${colindex}`">
                  <td
                    :class="[cellClass({ row, index, column }), { 'is-editing': isEditing(column.props.prop, index) }]"
                    :style="{ padding: isEditing(column.props.prop, index) ? '0' : padding, width: px(column.props.width), maxWidth: px(column.props.maxWidth) }"
                    @click="rowClick({ row, column, index, event: $event })"
                    @contextmenu="$emit('cellContextmenu', row, column, $event)"
                    @mousedown="focusRowIndex(index)"
                    @mousedown.middle="rowClickMiddle({ row, column, index, event: $event })"
                  >
                    <VTooltip
                      v-if="showOverflowTooltip(row, column.props)"
                      class="v-table__overflow-tooltip"
                      :disabled="column.props.hideOnExpand && isExpandedRow(row)"
                      lazy
                      :overflow-content-target="column.props.overflowContentTarget"
                      :overflow-target="column.props.overflowTarget ?? '.v-table__cell'"
                      show-on-overflow
                    >
                      <VTableCell
                        class="v-table__cell"
                        :class="column.classes"
                        :column
                        :props="{ row, index, colindex, toggleExpand, editing: isEditing(column.props.prop, index), expanded: isExpandedRow(row) }"
                        :style="{
                          minWidth: px(column.props.minWidth),
                          width: px(column.props.width),
                          padding: isEditing(column.props.prop, index) ? padding : undefined,
                        }"
                      />
                    </VTooltip>

                    <VTableCell
                      v-else
                      class="v-table__cell"
                      :class="column.classes"
                      :column
                      :props="{ row, index, colindex, toggleExpand, editing: isEditing(column.props.prop, index), expanded: isExpandedRow(row) }"
                      :style="{ minWidth: px(column.props.minWidth), padding: isEditing(column.props.prop, index) ? padding : undefined }"
                    />
                  </td>
                </template>

                <td v-if="expandable && expandableColumn && expandableRight" class="chevron-cell" :style="{ padding }" @click.stop="toggleExpand(row)">
                  <VIcon v-if="rowIsExpandable(row)" icon="ChevronRight" :size="12" />
                </td>

                <td
                  v-if="checkable && checkboxRight"
                  class="v-table__cell--checkbox"
                  :style="{ padding }"
                  @click.prevent.stop="checkOnCheckboxCellClick ? checkRow({ row, index, event: $event }) : ''"
                >
                  <VCheckbox :disabled="!isRowCheckable(row)" :model-value="isRowChecked(row)" @click.prevent.stop="checkRow({ row, index, event: $event })" />
                </td>
              </tr>

              <tr v-if="isExpandedRow(row)" class="v-table__expanded-row">
                <td v-if="expandColspanBefore" :colspan="expandColspanBefore" :style="{ padding }" />
                <td :colspan="columnCount - expandColspanBefore" :style="expandCellStyle">
                  <slot :index name="expand" :row />
                </td>
              </tr>
            </template>

            <tr v-if="paginatedData.length === 0 && !loading" class="is-empty">
              <td :colspan="columnCount">
                <slot name="empty">
                  <div class="color-grey-40">{{ i18n.noData }}</div>
                </slot>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Transition>

    <Transition name="v-fade">
      <template v-if="loading">
        <slot name="loading">
          <div class="v-table__loading">
            <VLoadingDots :size="8" />
          </div>
        </slot>
      </template>
    </Transition>

    <VPagination v-if="pagination" v-model="page" v-model:page-size="pageSize" :total="data.length" v-bind="pagination" />
  </div>
</template>

<script setup lang="ts" generic="TRow extends Record<string, any>">
// TRow should not contain refs, otherwise our ref will unwrap it, https://github.com/vuejs/core/issues/2136#issuecomment-693524663

import { get, range, throttle } from 'lodash-es'
import { computed, onBeforeUnmount, onMounted, provide, type Ref, ref, useTemplateRef, watch } from 'vue'

import { emptyDragImage } from '../../composables/useDraggable.ts'
import { useResizeObserver } from '../../composables/useResizeObserver.ts'
import { type SortOrder } from '../../types.ts'
import { delay, px, targetInOneOfSelectors } from '../../utils/helpers.ts'
import { i18nTable as i18n } from '../../utils/i18n.ts'
import { $tableKey } from '../../utils/injections.ts'
import { sortArrayByObjectKey } from '../../utils/sorting.ts'
import VIcon from '../Icon/VIcon.vue'
import VCheckbox from '../VCheckbox.vue'
import VLoadingDots from '../VLoadingDots.vue'
import VPagination, { type Props as PaginationProps } from '../VPagination.vue'
import VSortCarets from '../VSortCarets.vue'
import VTooltip from '../VTooltip.vue'
import VTableCell from './VTableCell.vue'
import { type TableColumn } from './VTableColumn.vue'

export interface EditCell {
  prop: string
  index: number
}

interface Props {
  cellClass?: (args: { row: TRow; index: number; column: TableColumn }) => any
  cellPadding?: number | string
  checkable?: boolean
  checkboxRight?: boolean
  checkedRows?: TRow[]
  checkOnCheckboxCellClick?: boolean
  checkOnDragover?: boolean
  checkOnRowClick?: boolean
  clickable?: boolean
  data?: TRow[]
  defaultSort?: { prop?: string; order?: 'asc' | 'desc' | '' }
  draggable?: boolean
  editableCells?: boolean
  expandable?: boolean
  expandableColumn?: boolean
  expandableRight?: boolean
  expandColspanBefore?: number
  expandOnRowClick?: boolean
  headerCheckable?: boolean
  height?: number | string
  hideEmptyColumns?: boolean
  hideHeader?: boolean
  isRowCheckable?: (row: TRow) => boolean
  layout?: 'auto' | 'fixed'
  loading?: boolean
  minHeight?: number
  noBorder?: boolean
  pagination?: PaginationProps & { page?: number; pageSize?: number }
  remoteSort?: boolean
  rowClass?: (row: TRow, index: number) => any
  rowIsExpandable?: (row: TRow) => boolean
  rowKey?: string
  stickyHeader?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  cellClass: () => '',
  cellPadding: 10,
  checkable: false,
  checkboxRight: false,
  checkedRows: () => [],
  checkOnCheckboxCellClick: false,
  checkOnDragover: false,
  checkOnRowClick: false,
  clickable: false,
  data: () => [],
  defaultSort: () => ({}),
  draggable: false,
  editableCells: false,
  expandable: false,
  expandableColumn: true,
  expandableRight: false,
  expandColspanBefore: 0,
  expandOnRowClick: true,
  headerCheckable: true,
  height: undefined,
  hideEmptyColumns: false,
  hideHeader: false,
  isRowCheckable: () => true,
  layout: 'auto',
  loading: false,
  minHeight: undefined,
  noBorder: false,
  pagination: undefined,
  remoteSort: false,
  rowClass: () => '',
  rowIsExpandable: () => true,
  rowKey: '',
  stickyHeader: false,
})

const emit = defineEmits<{
  checkAll: [checkedRows: TRow[]]
  check: [checkedRows: TRow[], row?: TRow]
  dragend: [args: { row: TRow; index: number; event: DragEvent }]
  dragenter: [args: { row: TRow; index: number; event: DragEvent }]
  dragleave: [args: { row: TRow; index: number; event: DragEvent }]
  dragover: [args: { row: TRow; index: number; event: DragEvent }]
  dragstart: [args: { row: TRow; index: number; event: DragEvent }]
  drop: [args: { row: TRow; index: number; event: DragEvent }]
  expandClose: [row: TRow]
  expandOpen: [row: TRow]
  rowClick: [args: { row: TRow; column: TableColumn; index: number; event: MouseEvent }]
  rowClickMiddle: [args: { row: TRow; column: TableColumn; index: number; event: MouseEvent }]
  sort: [args: { prop: string; order: SortOrder }]
  stopEditing: []
  dblclick: [row: TRow]
  mouseenter: [row: TRow]
  mouseleave: [row: TRow]
  cellContextmenu: [row: TRow, column: TableColumn, ev: MouseEvent]
  'update:checkedRows': [checkedRows: TRow[]]
}>()

defineSlots<{
  default: (props: any) => any
  empty: (props: any) => any
  expand: (props: { index: number; row: TRow }) => any
  loading: (props: any) => any
}>()

const editCell = defineModel<EditCell>('editCell')
const sortBy = defineModel<string>('sortBy', { default: '' })
const sortOrder = defineModel<SortOrder>('sortOrder', { default: '' })
// eslint-disable-next-line vue/no-ref-object-reactivity-loss
sortBy.value = sortBy.value || props.defaultSort.prop || ''
// eslint-disable-next-line vue/no-ref-object-reactivity-loss
sortOrder.value = sortOrder.value || props.defaultSort.order || ''

const newCheckedRows = ref(props.checkedRows.slice()) as Ref<TRow[]>
const page = ref(props.pagination?.page ?? 1)
const pageSize = ref(props.pagination?.pageSize ?? 10)
const expandedRows = ref([]) as Ref<(TRow | NonNullable<any>)[]>
const lastCheckedRowIndex = ref<null | number>(null)
const focusedRowIndex = ref<null | number>(null)
const currentDragoverIndex = ref<null | number>(null)
const lastDragoverIndex = ref<null | number>(null)
const startDragoverIndex = ref<null | number>(null)
const tableColumns = ref<TableColumn[]>([])
const recentlyExpanded = ref<any>()
const table = useTemplateRef('table')
const tableHeight = ref(props.minHeight)

useResizeObserver(
  table,
  throttle(({ height }) => {
    const newHeight = Math.ceil(table.value?.scrollHeight ?? height)
    if (newHeight !== tableHeight.value) {
      requestAnimationFrame(() => (tableHeight.value = newHeight))
    }
  }, 100),
)

const isDynamicHeight = computed(() => !props.height && !props.minHeight)

const wrapperHeight = computed(() => {
  if (!isDynamicHeight.value) {
    return undefined
  }

  return props.height ? props.height : tableHeight.value
})
const isAllUncheckable = computed(() => props.data.filter((row) => props.isRowCheckable(row)).length === 0)
const visibleColumns = computed(() =>
  tableColumns.value.filter((column) => {
    if (column.props.visible === false) {
      return false
    }

    if (props.hideEmptyColumns && column.props.prop && !props.data.some((row) => row[column.props.prop] !== undefined)) {
      return false
    }

    return true
  }),
)
const padding = computed(() => px(props.cellPadding))

const sortedData = computed(() => {
  if (!sortOrder.value || !sortBy.value || props.remoteSort) return props.data
  const currentSortColumn = tableColumns.value.find((column) => column.props.prop === sortBy.value)
  if (!currentSortColumn) return props.data

  return sortArrayByObjectKey({
    array: props.data,
    key: sortBy.value,
    order: sortOrder.value,
    transform: currentSortColumn.props.sortTransform,
    customTransform: currentSortColumn.props.customSortTransform,
  })
})

const paginatedData = computed(() => {
  if (props.pagination === undefined) return sortedData.value
  const end = page.value * pageSize.value

  return sortedData.value.slice(end - pageSize.value, end)
})

/**
 * Check if all rows in the page are checked.
 */
const isAllChecked = computed(() => {
  const checkedData = props.data.filter((row) => props.isRowCheckable(row))
  if (checkedData.length === 0) return false

  return !checkedData.some((currentVisibleRow) => !newCheckedRows.value.some((checked) => handleRowKey(checked) === handleRowKey(currentVisibleRow)))
})

/**
 * Return total column count based if it's checkable or expanded.
 */
const columnCount = computed(() => {
  let count = visibleColumns.value.length
  count += props.checkable ? 1 : 0
  count += props.expandableColumn ? 1 : 0

  return count
})

const expandCellStyle = computed(() => {
  const style = { padding: props.cellPadding }
  let index = props.expandColspanBefore
  if (props.expandable && props.expandableColumn && !props.expandableRight) {
    index--
  }
  if (props.checkable && !props.checkboxRight) {
    index--
  }
  const column = visibleColumns.value[index]

  if (column) {
    return {
      ...style,
      minWidth: px(column.props.minWidth),
      width: px(column.props.width),
      maxWidth: px(column.props.maxWidth),
    }
  }

  return style
})

const expandedRowCache = computed(() => {
  const cache = new Set<TRow>()

  for (const row of paginatedData.value) {
    if (expandedRows.value.includes(handleRowKey(row))) {
      cache.add(row)
    }
  }

  return cache
})

/**
 * When checkedRows prop change, update internal value without
 * mutating original data.
 */
watch(
  () => props.checkedRows,
  (rows) => {
    newCheckedRows.value = [...rows]

    if (rows.length === 0) {
      lastCheckedRowIndex.value = null
    }
  },
)

onMounted(() => {
  document.addEventListener('mouseup', onMouseUp, { passive: true })
})

onBeforeUnmount(() => document.removeEventListener('mouseup', onMouseUp))

function onMouseUp({ target }: MouseEvent) {
  unfocusRowIndex()
  if (
    editCell.value &&
    (!target || (!table.value?.contains(target as HTMLElement) && !targetInOneOfSelectors(target as HTMLElement, ['.v-datepicker__popper', '.v-popper--select'])))
  ) {
    editCell.value = undefined
    emit('stopEditing')
  }
}

function focusRowIndex(index: number) {
  focusedRowIndex.value = index
}

function unfocusRowIndex() {
  focusedRowIndex.value = null
}

function sortNext(prop: string) {
  if (sortBy.value === prop) {
    if (sortOrder.value === 'asc') {
      sortOrder.value = 'desc'
    } else if (prop === props.defaultSort.prop) {
      sortOrder.value = 'asc'
    } else {
      sortBy.value = props.defaultSort.prop ?? ''
      sortOrder.value = props.defaultSort.order ?? ''
    }
  } else {
    sortBy.value = prop
    sortOrder.value = 'asc'
  }

  emit('sort', { prop: sortBy.value, order: sortOrder.value })
}

function sort({ prop, order }: { prop: string; order: 'asc' | 'desc' | '' }) {
  if (!order) {
    sortBy.value = props.defaultSort.prop ?? ''
    sortOrder.value = props.defaultSort.order ?? ''
  } else {
    sortBy.value = prop
    sortOrder.value = order
  }

  emit('sort', { prop: sortBy.value, order: sortOrder.value })
}

function isRowChecked(row: TRow) {
  const rowKey = props.rowKey
  if (rowKey) {
    return newCheckedRows.value.some((checked) => get(checked, rowKey, checked) === get(row, rowKey, row))
  }

  return newCheckedRows.value.includes(row)
}

/**
 * Remove a checked row from the array.
 */
function removeCheckedRow(row: TRow) {
  const index = newCheckedRows.value.findIndex((checked) => handleRowKey(checked) === handleRowKey(row))
  if (index >= 0) {
    newCheckedRows.value.splice(index, 1)
  }
}

/**
 * VHeader checkbox click listener.
 * Add or remove all rows in current page.
 */
function checkAll() {
  const wasAllChecked = isAllChecked.value
  for (const row of props.data) {
    if (props.isRowCheckable(row)) {
      removeCheckedRow(row)
    }

    if (!wasAllChecked) {
      if (props.isRowCheckable(row)) {
        newCheckedRows.value.push(row)
      }
    }
  }

  emit('check', newCheckedRows.value)
  emit('checkAll', newCheckedRows.value)

  // Emit checked rows to update user variable.
  emit('update:checkedRows', newCheckedRows.value)
}

/**
 * VGrid checkbox click listener.
 */
function checkRow({ row, index, event }: { row: TRow; index: number; event: { shiftKey?: boolean } }) {
  if (!props.isRowCheckable(row)) return
  const lastIndex = lastCheckedRowIndex.value
  lastCheckedRowIndex.value = index

  if (event.shiftKey && lastIndex !== null && index !== lastIndex) {
    shiftCheckRow({ row, index, lastIndex })
  } else if (!isRowChecked(row)) {
    newCheckedRows.value.push(row)
  } else {
    removeCheckedRow(row)
  }

  emit('check', newCheckedRows.value, row)
  emit('update:checkedRows', newCheckedRows.value)
}

/**
 * Check row when shift is pressed.
 */
function shiftCheckRow({ row, index, lastIndex }: { row: TRow; index: number; lastIndex: number }) {
  // Get the subset of the list between the two indicies
  const subset = props.data.slice(Math.min(index, lastIndex), Math.max(index, lastIndex) + 1)

  // Determine the operation based on the state of the clicked checkbox.
  const shouldCheck = !isRowChecked(row)

  subset.forEach((item) => {
    removeCheckedRow(item)
    if (shouldCheck && props.isRowCheckable(item)) {
      newCheckedRows.value.push(item)
    }
  })
}

function rowClick({ row, column, index, event }: { row: TRow; column: TableColumn; index: number; event: MouseEvent }) {
  emit('rowClick', { row, column, index, event })

  if (props.editableCells) {
    editCell.value = { prop: column.props.prop, index }
  }

  if (props.checkOnRowClick) {
    checkRow({ row, index, event })
  }

  if (props.expandable && props.expandOnRowClick && props.rowIsExpandable(row)) {
    toggleExpand(row)
  }
}

function rowClickMiddle({ row, column, index, event }: { row: TRow; column: TableColumn; index: number; event: MouseEvent }) {
  emit('rowClickMiddle', { row, column, index, event })
}

function toggleExpand(row: TRow) {
  if (!props.rowIsExpandable(row)) {
    return
  }

  // Can't use isExpandedRow here, because we might be toggling a row that is not in the current page.
  const expanded = expandedRows.value.includes(handleRowKey(row))

  if (expanded) {
    closeExpandRow(row)
    emit('expandClose', row)
  } else {
    openExpandRow(row)
    emit('expandOpen', row)
  }
}

function openExpandRow(row: TRow, showRecentlyExpanded = false) {
  const key = handleRowKey(row)

  if (!expandedRows.value.includes(key)) {
    expandedRows.value.push(key)

    if (showRecentlyExpanded) {
      recentlyExpanded.value = key

      delay(2000).then(() => {
        if (recentlyExpanded.value === key) {
          recentlyExpanded.value = undefined
        }
      })
    }
  }
}

function closeExpandRow(row: TRow) {
  const index = expandedRows.value.indexOf(handleRowKey(row))
  expandedRows.value.splice(index, 1)
}

function isExpandedRow(row: TRow) {
  return expandedRowCache.value.has(row)
}

function handleRowKey(row: TRow) {
  return props.rowKey ? (get(row, props.rowKey) ?? row) : row
}

/**
 * When you start dragging, reset the checked rows, and store the index of the row that you started dragging on.
 */
function onDragstart(row: TRow, index: number, event: DragEvent) {
  if (props.checkable && props.checkOnDragover) {
    event.dataTransfer?.setDragImage(emptyDragImage, 0, 0)
  }

  emit('update:checkedRows', [])
  startDragoverIndex.value = index

  emit('dragstart', { row, index, event })
}

/**
 * When you stop dragging, reset the stored indices.
 */
function onDragend(row: TRow, index: number, event: DragEvent) {
  startDragoverIndex.value = null
  currentDragoverIndex.value = null
  lastDragoverIndex.value = null
  emit('dragend', { row, index, event })
}

function onDrop(row: TRow, index: number, event: DragEvent) {
  emit('drop', { row, index, event })
}

/**
 * Every time you drag over a row, make sure all rows between the start index and the current index are checked.
 */
function onDragover(row: TRow, index: number, event: DragEvent) {
  emit('dragover', { row, index, event })
}

/**
 * Every time you drag over a row, make sure all rows between the start index and the current index are checked.
 */
function onDragenter(row: TRow, index: number, event: DragEvent) {
  currentDragoverIndex.value = index

  if (props.checkable && props.checkOnDragover) {
    if (index === startDragoverIndex.value) {
      newCheckedRows.value = []
      emit('update:checkedRows', [])
      emit('dragenter', { row, index, event })

      return
    }

    if (!isRowChecked(row)) {
      for (const i of range(startDragoverIndex.value ?? 0, index)) {
        if (!isRowChecked(paginatedData.value[i])) {
          checkRow({ row: paginatedData.value[i], index: i, event: {} })
        }
      }
      checkRow({ row: paginatedData.value[index], index, event })
    }
  }

  emit('dragenter', { row, index, event })
}

/**
 * When you leave a row, and are moving towards the start index, the row should be unchecked.
 */
function onDragleave(row: TRow, index: number, event: DragEvent) {
  if (index === startDragoverIndex.value) {
    emit('dragleave', { row, index, event })

    return
  }

  if (
    lastDragoverIndex.value !== null &&
    (((startDragoverIndex.value ?? 0) <= index && lastDragoverIndex.value > index) || ((startDragoverIndex.value ?? 0) >= index && lastDragoverIndex.value < index))
  ) {
    checkRow({ row: paginatedData.value[lastDragoverIndex.value], index, event })
  }

  lastDragoverIndex.value = index
  emit('dragleave', { row, index, event })
}

function isEditing(prop: string, index: number): boolean {
  return !!(props.editableCells && editCell.value && index === editCell.value.index && prop === editCell.value.prop)
}

function showOverflowTooltip(row: TRow, props: TableColumn['props']): boolean {
  if (!props.showOverflowTooltip) {
    return false
  }

  if (typeof props.showOverflowTooltip === 'function') {
    return props.showOverflowTooltip(row)
  }

  return true
}

provide($tableKey, {
  tableColumns,
})

defineExpose({
  checkAll,
  checkRow,
  emit,
  expandedRows,
  lastDragoverIndex,
  newCheckedRows,
  onDragend,
  onDragleave,
  onDragover,
  onDragstart,
  onDrop,
  page,
  pageSize,
  rowClick,
  sort,
  sortBy,
  startDragoverIndex,
  toggleExpand,
})
</script>

<style>
.v-table__wrapper {
  overflow-x: auto;

  &.v-table__wrapper--dynamic-height {
    transition: height var(--transition);
    overflow-y: hidden;
  }
}

.v-table {
  min-width: 100%;
  overflow: auto;
  position: relative;
  margin-left: -5px; /* To compensate for the padding on v-table-inner */
  margin-top: -5px;

  &.is-loading {
    min-height: 50px;
  }

  &.has-sticky-header {
    .v-table__wrapper {
      overflow-y: auto;
      overflow-y: overlay; /* Only some browsers support this */
    }

    tr:first-child th {
      position: sticky;
      top: 0;
      z-index: 2;
      background: #fff;
    }
  }

  &.expand-on-row-click tr:hover td.chevron-cell .v-icon *,
  td.chevron-cell:hover .v-icon * {
    stroke: var(--color-primary);
  }
}

.v-table__loading {
  display: flex;
  position: absolute;
  align-items: center;
  justify-content: center;
  z-index: 1;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  background-color: rgba(255, 255, 255, 0.7);
}

.v-table__inner {
  border-spacing: 0;
  width: 100%;
  padding: 5px; /* To make sure that box-shadow on tr.clickable works properly */
  border-collapse: separate;

  th {
    font-size: 14px;
    font-weight: 400;
    text-align: left;

    .th-wrap.is-sortable .is-relative {
      user-select: none;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
    }
  }

  tr.is-dragging * {
    pointer-events: none;
  }

  tr.clickable {
    cursor: pointer;

    &:hover {
      box-shadow: 0 0 5px 0 rgb(0 0 0 / 25%);
      border-radius: var(--border-radius);
    }

    &.is-focused {
      box-shadow: inset 0 0 3px 0 rgb(0 0 0 / 25%);
      border-radius: var(--border-radius);
    }
  }

  tr.is-expanded {
    td {
      border-bottom-color: transparent;
    }

    .v-table__cell.hide-on-expand {
      visibility: hidden;
    }

    .chevron-cell .v-icon-chevronright {
      transform: rotate(90deg);
    }

    &.is-recently-expanded {
      td {
        background-color: var(--color-blue-1);
      }
    }
  }

  tr.is-empty td {
    text-align: center;
    padding: 30px 10px;
    border-bottom: none;
  }

  td {
    font-size: 16px;
    line-height: 20px;
    border-bottom: var(--border-light);
    transition: background-color 1s ease;

    &.chevron-cell {
      width: 35px;
    }

    &.has-errors {
      background-color: #fff5f5;
    }
  }

  tbody > tr:first-child td {
    border-top: var(--border-light);
  }

  tr:has(+ tr.clickable:hover) td,
  tr:has(+ tr.clickable.is-focused) td {
    border-bottom-color: transparent;
  }

  .v-table__cell--checkbox {
    width: 50px;
  }

  .v-table__overflow-tooltip .v-table__cell {
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .v-table__cell {
    word-break: break-word;
    white-space: nowrap;

    &.wrap-text {
      white-space: normal;
    }
  }
}

.v-table__expanded-row {
  word-break: break-word;
}

.v-table--editable-cells td {
  border-right: var(--border-light);

  &:first-child {
    border-left: var(--border-light);
  }

  &:hover {
    box-shadow: 0 0 6px 0 rgb(0, 0, 0, 0.2);
    filter: drop-shadow(0 0 3px rgb(155, 155, 155, 0.1));
  }

  &.is-editing .v-table__cell {
    border: 1px solid var(--color-primary);
    margin: -1px;
  }

  .v-input.is-minimal {
    .v-input__inner {
      min-height: 30px;
      font-size: inherit;
      padding-top: 0;
      padding-bottom: 0;
    }

    &:not(.has-prefix) .v-input__inner {
      padding-left: 0 !important; /* Overrides manual style in v-input. */
    }

    &:not(.has-suffix) .v-input__inner {
      padding-right: 0 !important; /* Overrides manual style in v-input. */
    }
  }

  .v-select {
    width: 100%;

    .v-input__value {
      padding-top: 0;
    }
  }
}

.v-card--primary .v-table__inner {
  td {
    border-color: var(--color-blue-4);
  }

  tbody > tr:first-child td {
    border-top: 1px solid var(--color-blue-4);
  }
}

.v-card--info .v-table__inner {
  td {
    border-color: var(--color-grey-20);
  }

  tbody > tr:first-child td {
    border-top: 1px solid var(--color-grey-20);
  }
}

.v-table:not(.v-table--bordered) {
  .v-table__inner tbody > tr:first-child td {
    border-top: none;
  }

  td {
    border: none;
    font-weight: 500;
  }
}

.v-card--primary .v-table__loading {
  background-color: rgba(240, 247, 254, 0.7);
}

.v-card--info .v-table__loading {
  background-color: rgba(240, 240, 240, 0.7);
}

@media (max-width: 768px) {
  .v-table__inner td .v-table__cell {
    word-break: keep-all;
    text-overflow: clip;
    overflow: hidden;
  }
}
</style>
